import {
	Button,
	CategorySelect,
	Checkbox,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	categoryName,
	Info,
	Label,
	NativeSelect,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	useCategories,
} from "@ondit/ui";
import { useState } from "react";
import { Link, useParams } from "react-router";
import {
	Async,
	Badge,
	Empty,
	KV,
	PageHead,
	Pager,
	SearchBox,
	Segmented,
	Status,
	useAction,
	useConfirm,
	useLines,
} from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Entity, type EntityDetail, type Me } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync, useDebounced, usePaged } from "@/lib/hooks";

export function EntitiesPage() {
	const t = useT();
	const { locale } = useI18n();
	const [q, setQ] = useState("");
	const [status, setStatus] = useState("");
	const [filter, setFilter] = useState<"" | "claimed" | "unclaimed">("");
	const query = useDebounced(q);
	const { state, rows, hasMore, loadingMore, more } = usePaged(
		(offset, limit) => api.entities({ q: query, status, filter, limit, offset }),
		(page) => page.entities,
		[query, status, filter],
	);

	return (
		<>
			<PageHead title={t("entities.title")} help={t("entities.lede")} />
			<div className="table-tools">
				<SearchBox value={q} onChange={setQ} />
				<NativeSelect aria-label={t("state.status")} value={status} onChange={(e) => setStatus(e.target.value)}>
					<option value="">{t("state.all")}</option>
					{["draft", "listed", "hidden", "merged"].map((s) => (
						<option key={s} value={s}>
							{t(`entities.status.${s}`)}
						</option>
					))}
				</NativeSelect>
				<Segmented
					value={filter}
					onChange={setFilter}
					options={[
						{ value: "", label: t("state.all") },
						{ value: "claimed", label: t("entities.filter.claimed") },
						{ value: "unclaimed", label: t("entities.filter.unclaimed") },
					]}
				/>
			</div>
			<Async state={state}>
				{({ total }) =>
					rows.length === 0 ? (
						<Empty />
					) : (
						<>
							<div className="table-scroll">
								<Table className="text-[length:var(--t-sm)]">
									<TableHeader>
										<TableRow>
											<TableHead>{t("entities.name")}</TableHead>
											<TableHead>{t("state.status")}</TableHead>
											<TableHead>{t("entities.domains")}</TableHead>
											<TableHead>{t("entities.owner")}</TableHead>
											<TableHead className="num text-right whitespace-nowrap">{t("entities.datapoints")}</TableHead>
											<TableHead className="num text-right whitespace-nowrap">{t("entities.published")}</TableHead>
											<TableHead>{t("entities.source")}</TableHead>
											<TableHead>{t("state.updated")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{rows.map((e: Entity) => (
											<TableRow key={e.id}>
												<TableCell>
													<Link to={`/entities/${e.id}`}>{e.name}</Link>
													<span className="sub mono">{e.slug}</span>
												</TableCell>
												<TableCell>
													<div className="chips">
														<Status value={e.status} prefix="entities.status" />
														{e.paused ? <Badge tone="warning">{t("entities.paused")}</Badge> : null}
													</div>
												</TableCell>
												<TableCell className="mono small">{e.domains.join(", ") || "—"}</TableCell>
												<TableCell>
													{e.owner_org_id ? (
														<Link to={`/orgs/${e.owner_org_id}`}>{e.owner_name ?? e.owner_org_id}</Link>
													) : (
														<span className="faint">{t("entities.unclaimed")}</span>
													)}
												</TableCell>
												<TableCell className="num text-right whitespace-nowrap">{e.datapoint_count}</TableCell>
												<TableCell className="num text-right whitespace-nowrap">{e.published_pages}</TableCell>
												<TableCell className="faint">{e.source ?? "—"}</TableCell>
												<TableCell className="whitespace-nowrap">{fmt.date(locale, e.updated_at)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<Pager shown={rows.length} total={total} hasMore={hasMore} busy={loadingMore} onMore={more} />
						</>
					)
				}
			</Async>
		</>
	);
}

export function EntityPage() {
	const { id = "" } = useParams();
	const t = useT();
	const { locale } = useI18n();
	const state = useAsync<EntityDetail>(() => api.entity(id), [id]);
	const me = useAsync<Me>(() => api.me(), []);
	const confirm = useConfirm();
	const { run, busy } = useAction();
	const categories = useCategories();
	const [editing, setEditing] = useState(false);

	return (
		<Async state={state}>
			{(d) => {
				const e = d.entity;
				const publicUrl = `${me.data?.origins.public ?? ""}/e/${e.slug}`;
				return (
					<>
						<PageHead
							title={e.name}
							lede={
								<span className="chips">
									<Status value={e.status} prefix="entities.status" />
									{e.paused ? <Badge tone="warning">{t("entities.paused")}</Badge> : null}
									{e.owner_org_id ? (
										<Badge tone="neutral">
											<Link to={`/orgs/${e.owner_org_id}`} style={{ color: "inherit" }}>
												{e.owner_name ?? e.owner_org_id}
											</Link>
										</Badge>
									) : (
										<Badge tone="outline">{t("entities.unclaimed")}</Badge>
									)}
								</span>
							}
							crumbs={[{ to: "/entities", label: t("entities.title") }]}
						>
							<Button asChild variant="default">
								<a href={publicUrl} target="_blank" rel="noreferrer">
									{t("entities.openPublic")}
								</a>
							</Button>
							<Button variant="primary" type="button" onClick={() => setEditing(true)}>
								{t("entities.edit")}
							</Button>
							{e.owner_org_id ? (
								<Button
									variant="destructive"
									type="button"
									disabled={busy}
									onClick={async () => {
										const ok = await confirm(
											t("entities.release"),
											`${t("entities.releaseConfirm", { org: e.owner_name ?? e.owner_org_id ?? "" })} ${t("entities.releaseHint")}`,
											true,
										);
										if (ok) {
											const r = await run(
												() => api.releaseEntity(e.id),
												(r) => t("entities.released", { n: r.drafts_dropped }),
											);
											if (r) state.reload();
										}
									}}
								>
									{t("entities.release")}
								</Button>
							) : null}
						</PageHead>

						<div className="grid-2 detail-grid">
							<div className="card">
								<KV
									items={[
										[t("state.id"), <span className="mono">{e.id}</span>],
										["slug", <span className="mono">{e.slug}</span>],
										[t("entities.website"), e.website],
										[
											t("entities.categories"),
											d.categories.map((slug) => categoryName(categories, slug, locale) ?? slug).join(" · ") || null,
										],
										[t("entities.source"), `${e.source ?? "—"}${d.source_ref ? ` · ${d.source_ref}` : ""}`],
										[t("entities.datapoints"), e.datapoint_count],
										[t("state.created"), fmt.dateTime(locale, e.created_at)],
										[t("state.updated"), fmt.dateTime(locale, e.updated_at)],
										[
											t("entities.measurement"),
											`${t("entities.questionSets", { n: d.measurement.question_sets })} · ${t("entities.citations30", { n: d.measurement.citations_30d })} · ${t("entities.lastCited", { when: fmt.relative(locale, d.measurement.last_cited_at) ?? t("state.never") })}`,
										],
									]}
								/>
							</div>

							<div className="data-section data-section-wide">
								<h2>
									{t("entities.datapointsTitle")} <Info>{t("entities.datapointsHint")}</Info>
								</h2>
								{Object.keys(d.datapoints).length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table className="text-[length:var(--t-sm)]">
										<TableHeader>
											<TableRow>
												<TableHead>{t("entities.dpKey")}</TableHead>
												<TableHead>{t("entities.dpValue")}</TableHead>
												<TableHead>{t("entities.dpSource")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{Object.entries(d.datapoints).map(([key, dp]) => (
												<TableRow key={key}>
													<TableCell className="mono">{key}</TableCell>
													<TableCell className="cell-prose">
														{String(dp.value ?? "")}
														{dp.unit ? ` ${dp.unit}` : ""}
													</TableCell>
													<TableCell className="small cell-source">
														{dp.source_url ? (
															<a href={dp.source_url} target="_blank" rel="noreferrer">
																{dp.source_url}
															</a>
														) : (
															<Badge tone="danger">{t("state.none")}</Badge>
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>

							<div className="data-section data-section-wide">
								<h2>{t("entities.pages")}</h2>
								{d.pages.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table className="text-[length:var(--t-sm)]">
										<TableBody>
											{d.pages.map((p) => (
												<TableRow key={p.id}>
													<TableCell>
														<Link to={`/pages/${p.id}`}>
															{p.kind}/{p.slug}
														</Link>
														<span className="sub">{p.locale}</span>
													</TableCell>
													<TableCell>
														<Status value={p.status} prefix="pages.status" />
													</TableCell>
													<TableCell className="whitespace-nowrap">{fmt.date(locale, p.published_at)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>

							<div className="data-section">
								<h2>{t("entities.claims")}</h2>
								{d.claims.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table className="text-[length:var(--t-sm)]">
										<TableBody>
											{d.claims.map((c) => (
												<TableRow key={c.id}>
													<TableCell>
														<Link to={`/orgs/${c.org_id}`}>{c.org_id}</Link>
														<span className="sub mono">{c.domain}</span>
													</TableCell>
													<TableCell>
														<Badge tone={c.status === "verified" ? "success" : "warning"}>{c.status}</Badge>
														{c.method ? <span className="sub">{c.method}</span> : null}
													</TableCell>
													<TableCell className="whitespace-nowrap">{fmt.date(locale, c.created_at)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
						</div>

						{editing ? (
							<EditModal
								detail={d}
								onClose={() => setEditing(false)}
								onSaved={() => {
									setEditing(false);
									state.reload();
								}}
							/>
						) : null}
					</>
				);
			}}
		</Async>
	);
}

function EditModal({ detail, onClose, onSaved }: { detail: EntityDetail; onClose: () => void; onSaved: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { run, busy } = useAction();
	const e = detail.entity;
	const [name, setName] = useState(e.name);
	const [status, setStatus] = useState(e.status);
	const [website, setWebsite] = useState(e.website ?? "");
	const [categories, setCategories] = useState<string[]>(detail.categories);
	const [domains, setDomains, domainList] = useLines(e.domains);
	const [aliases, setAliases, aliasList] = useLines(detail.aliases);
	const [taglineEn, setTaglineEn] = useState(detail.tagline_en ?? "");
	const [taglineZh, setTaglineZh] = useState(detail.tagline_zh ?? "");
	const [paused, setPaused] = useState(e.paused);

	async function submit() {
		const r = await run(
			() =>
				api.updateEntity(e.id, {
					name: name !== e.name ? name : undefined,
					status: status !== e.status ? status : undefined,
					website: website !== (e.website ?? "") ? website : undefined,
					categories: categories.join("\n") !== detail.categories.join("\n") ? categories : undefined,
					domains: domainList.join("\n") !== e.domains.join("\n") ? domainList : undefined,
					aliases: aliasList.join("\n") !== detail.aliases.join("\n") ? aliasList : undefined,
					tagline_en: taglineEn !== (detail.tagline_en ?? "") ? taglineEn : undefined,
					tagline_zh: taglineZh !== (detail.tagline_zh ?? "") ? taglineZh : undefined,
					paused: paused !== e.paused ? paused : undefined,
				}),
			() => t("state.saved"),
		);
		if (r) onSaved();
	}

	return (
		<ChipModal open size="lg" dismissDisabled={busy} srTitle={t("entities.edit")} onOpenChange={onClose}>
			<ChipModalHeader onClose={onClose}>{t("entities.edit")}</ChipModalHeader>
			<ChipModalBody>
				<fieldset className="form" disabled={busy}>
					<ChipModalField type="input" title={t("entities.name")} required value={name} onChange={setName} />
					<ChipModalField
						type="dropdown"
						title={t("state.status")}
						value={status}
						onChange={setStatus}
						options={[
							...["draft", "listed", "hidden"].map((s) => ({ value: s, label: t(`entities.status.${s}`) })),
							...(status === "merged" ? [{ value: "merged", label: t("entities.status.merged") }] : []),
						]}
					/>
					<ChipModalField
						type="input"
						inputType="url"
						title={t("entities.website")}
						value={website}
						onChange={setWebsite}
					/>
					<ChipModalField type="custom" title={t("entities.categories")}>
						<CategorySelect
							value={categories}
							onChange={setCategories}
							locale={locale}
							labels={{
								label: t("entities.categories"),
								placeholder: t("category.placeholder"),
								search: t("category.search"),
								empty: t("category.empty"),
								loading: t("category.loading"),
								back: t("category.back"),
								whole: (name) => t("category.whole", { name }),
								full: (max) => t("category.full", { max }),
							}}
						/>
					</ChipModalField>
					<ChipModalField
						type="textarea"
						title={t("entities.domains")}
						hint={t("entities.listHint")}
						rows={4}
						value={domains}
						onChange={setDomains}
					/>
					<ChipModalField
						type="textarea"
						title={t("entities.aliases")}
						hint={t("entities.listHint")}
						rows={4}
						value={aliases}
						onChange={setAliases}
					/>
					<ChipModalField type="input" title={t("entities.tagline_en")} value={taglineEn} onChange={setTaglineEn} />
					<ChipModalField type="input" title={t("entities.tagline_zh")} value={taglineZh} onChange={setTaglineZh} />
					<ChipModalField type="custom" title={t("entities.paused")}>
						<Label className="check">
							<Checkbox checked={paused} onCheckedChange={(checked) => setPaused(checked === true)} />
							{t("entities.paused")}
						</Label>
					</ChipModalField>
				</fieldset>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={t("state.cancel")}
				onCancel={onClose}
				primaryAction={{ label: t("state.save"), onClick: () => void submit(), disabled: busy || !name.trim() }}
			/>
		</ChipModal>
	);
}
